import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { pollTask } from "../lib/tasks";
import { toast } from "sonner";
import { fmtDate, fmtNumber } from "../lib/format";
import { UploadSimple, FileText, ArrowRight, Sparkle } from "@phosphor-icons/react";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/documents");
      setDocs(r.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    toast.info("Analyse IA en cours — patientez ~1 min");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const taskId = r.data.task_id;
      const final = await pollTask(taskId, { intervalMs: 2500, timeoutMs: 180000 });
      if (final.status === "error") throw new Error(final.error || "Erreur IA");
      const doc = await api.get(`/documents/${final.result_id}`);
      toast.success("Document analysé");
      setSelected({ ...doc.data, extrait: doc.data.extrait_texte });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erreur d'upload/analyse");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const view = async (id) => {
    const r = await api.get(`/documents/${id}`);
    setSelected({ ...r.data, extrait: r.data.extrait_texte });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up">
        <div className="label-mono">§ 03 · Vita-Parser</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Parseur intelligent</h1>
        <p className="mt-3 text-[var(--muted)] max-w-2xl">Importez un document budgétaire, financier ou juridique au format PDF. L'IA extrait les agrégats clés et propose une qualification.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-black/15 border border-black/15 mb-8">
        {/* Upload zone */}
        <div className="lg:col-span-5 bg-[var(--paper)] p-8 fade-up">
          <div className="label-mono mb-3">§ Importation</div>
          <div className="border-2 border-dashed border-black/30 p-10 text-center bg-[var(--paper-2)]">
            <UploadSimple size={42} weight="duotone" className="mx-auto text-[var(--signal)]" />
            <div className="mt-4 font-serif text-2xl">Déposez un PDF</div>
            <div className="text-sm text-[var(--muted)] mt-1">Loi de finances · registre de la dette · bilan · convention de prêt</div>
            <input ref={fileRef} type="file" accept="application/pdf" onChange={onUpload} className="hidden" data-testid="upload-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-trigger"
              className="mt-6 inline-flex bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-5 py-3 label-mono items-center gap-2 disabled:opacity-60">
              <Sparkle size={14} weight="fill" /> {uploading ? "Analyse IA en cours..." : "Choisir un fichier PDF"}
            </button>
            <div className="mt-3 text-xs font-mono text-[var(--muted)]">Max 15 Mo · IA: Claude Sonnet 4.5</div>
          </div>
        </div>

        {/* Documents list */}
        <div className="lg:col-span-7 bg-[var(--paper)] p-6">
          <div className="label-mono mb-3">§ Pièces archivées</div>
          {loading ? (
            <div className="text-center py-12 label-mono">Chargement...</div>
          ) : docs.length === 0 ? (
            <div className="text-center py-12 label-mono text-[var(--muted)]">Aucun document analysé</div>
          ) : (
            <ul className="divide-y divide-black/10">
              {docs.map((d) => (
                <li key={d.id} className="py-4 flex items-center justify-between hover:bg-[var(--paper-2)] px-2" data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    <FileText size={20} className="text-[var(--signal)] shrink-0 mt-1" />
                    <div className="min-w-0">
                      <div className="font-serif text-lg truncate">{d.filename}</div>
                      <div className="font-mono text-xs text-[var(--muted)] mt-0.5">{fmtDate(d.cree_le)} · {(d.taille/1024).toFixed(0)} Ko · {d.analyse?.type_document || "document"}</div>
                    </div>
                  </div>
                  <button onClick={() => view(d.id)} className="label-mono hover:text-[var(--signal)] flex items-center gap-1" data-testid={`doc-view-${d.id}`}>
                    Lire <ArrowRight size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && (
        <div className="border border-black bg-[var(--paper)] mb-8 fade-up" data-testid="doc-detail">
          <div className="border-b border-black p-6 flex items-center justify-between">
            <div>
              <div className="label-mono">§ Analyse vitaliste</div>
              <h2 className="font-serif text-2xl mt-1">{selected.filename}</h2>
            </div>
            <button onClick={() => setSelected(null)} className="label-mono hover:text-[var(--signal)]">× Fermer</button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-black/15">
            <div className="bg-[var(--paper)] p-6">
              <div className="label-mono">Type</div>
              <div className="font-serif text-xl mt-1">{selected.analyse?.type_document || "—"}</div>
              <div className="mt-4 label-mono">Année</div>
              <div className="font-serif text-xl mt-1">{selected.analyse?.annee || "—"}</div>
              <div className="mt-4 label-mono">Devise</div>
              <div className="font-serif text-xl mt-1">{selected.analyse?.devise || "—"}</div>
            </div>
            <div className="bg-[var(--paper)] p-6 lg:col-span-2">
              <div className="label-mono">Résumé</div>
              <p className="mt-2 font-serif italic">{selected.analyse?.resume || "—"}</p>
              {selected.analyse?.anomalies_detectees?.length > 0 && (
                <>
                  <div className="mt-5 label-mono text-[var(--signal)]">Anomalies détectées</div>
                  <ul className="mt-2 space-y-1 list-disc pl-5 text-sm">
                    {selected.analyse.anomalies_detectees.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </>
              )}
            </div>
          </div>

          <div className="border-t border-black/15 p-6">
            <div className="label-mono mb-3">Agrégats financiers extraits</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-black/15 border border-black/15">
              {[
                ["Prélèvements obligatoires", selected.analyse?.prelevements_obligatoires],
                ["Dette nationale", selected.analyse?.dette_nationale],
                ["Service de la dette", selected.analyse?.service_dette_annuel],
                ["Intérêts annuels", selected.analyse?.interets_dette_annuels],
                ["Budget annuel", selected.analyse?.budget_annuel],
              ].map(([k, v]) => (
                <div key={k} className="bg-[var(--paper)] p-4">
                  <div className="label-mono text-[10px]">{k}</div>
                  <div className="mt-2 font-serif text-xl truncate">{v ? fmtNumber(v) : "—"}</div>
                </div>
              ))}
            </div>
          </div>

          {selected.analyse?.allocations_sectorielles && (
            <div className="border-t border-black/15 p-6">
              <div className="label-mono mb-3">Allocations sectorielles extraites</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-black/15 border border-black/15">
                {Object.entries(selected.analyse.allocations_sectorielles).map(([k, v]) => (
                  <div key={k} className="bg-[var(--paper)] p-4">
                    <div className="label-mono text-[10px] capitalize">{k}</div>
                    <div className="mt-2 font-serif text-lg">{v ? fmtNumber(v) : "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected.extrait && (
            <details className="border-t border-black/15">
              <summary className="p-6 label-mono cursor-pointer hover:text-[var(--signal)]">§ Extrait du texte (1500 caractères)</summary>
              <pre className="px-6 pb-6 text-xs font-mono whitespace-pre-wrap text-[var(--muted)]">{selected.extrait}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
